import React, { Suspense } from 'react';
import { SideRails } from './parts/SideRails';
import { OverlayDev } from './parts/OverlayDev';

interface ReferenceChromeProps {
  children: React.ReactNode;
}

/**
 * Page-level wrapper that renders the two vertical side rails
 * and is the `relative` anchor for all absolute decorations.
 */
export function ReferenceChrome({ children }: ReferenceChromeProps) {
  return (
    <div className="relative min-h-screen w-full bg-white" style={{ overflowX: 'clip' }}>
      <SideRails />
      {/* Dev overlay: visit /?overlay=0.25 to compare vs reference screenshot */}
      <Suspense>
        <OverlayDev />
      </Suspense>
      <div className="relative z-10">{children}</div>
    </div>
  );
}

/**
 * Shared inner container used by every section for consistent max-width + gutters.
 */
export function Container({
  children,
  className = '',
  wide = false,
}: {
  children: React.ReactNode;
  className?: string;
  wide?: boolean;
}) {
  return (
    <div
      className={`mx-auto w-full px-5 sm:px-8 lg:px-10 ${wide ? 'max-w-7xl' : 'max-w-6xl'} ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * Section label — small uppercase eyebrow text used above section headings.
 */
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex w-fit items-center rounded-md border border-[#129990]/25 bg-[#129990]/[0.07] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest text-[#129990]">
      {children}
    </span>
  );
}

/**
 * Horizontal divider used between major sections.
 * Constrained to the content column (max-w-6xl) so it never bleeds into the gutter stripes.
 */
export function SectionRule() {
  return <div className="mx-auto w-full max-w-6xl border-t border-gray-100" />;
}

/**
 * @deprecated Use SectionRule for section-level dividers.
 */
export function SectionDivider() {
  return <div className="border-t border-gray-100" />;
}
